import { Box } from '@mui/material';
import { useMemo, useEffect } from 'react';
import { Maybe } from '@/types/generated';
import { allowedYextOrigins } from '@shared-components/constants';
import { dataLayerConfig, IDataLayerEventConfig } from '@utils/dataLayerConfig';
export interface IFrame {
  /**
   * Required field for the iframe title
   *
   * @type {string}
   * @memberof IFrame
   */
  title: string;
  /**
   * Required field to identify the webpage to be embedded in the iframe
   *
   * @type {string}
   * @memberof IFrame
   */
  url: string;
  /**
   * Optional field to define a specific height for the iframe
   *
   * @type {Maybe<number>}
   * @memberof IFrame
   */
  height?: Maybe<number>;
  /**
   * Optional data-testid attribute for testing purposes.
   *
   * @type {string}
   * @memberof IMultiCard
   */
  dataTestId?: string;
  /***
   *  html string
   *
   */
  rawHtml?: string;
}
export default function IFrame({
  dataTestId = 'iframe_container',
  url = '',
  height,
  title = '',
  rawHtml
}: Readonly<IFrame>) {
  const iFrameHeight = height ? `${height}px` : '100vh';
  const hasRawHtml = Boolean(rawHtml);
  const sanitizedRawHtml = useMemo(() => {
    if (!rawHtml || globalThis.window === undefined) return '';
    const parsed = new DOMParser().parseFromString(rawHtml, 'text/html');
    parsed.querySelectorAll('script, iframe, object, embed, link[rel="import"]').forEach(node => node.remove());
    parsed.querySelectorAll('*').forEach(el => {
      for (const attr of Array.from(el.attributes)) {
        const name = attr.name.toLowerCase();
        const value = attr.value.trim().toLowerCase();
        if (name.startsWith('on') || value.startsWith('javascript:')) {
          el.removeAttribute(attr.name);
        }
      }
    });
    return parsed.body.innerHTML;
  }, [rawHtml]);
  const rawHtmlContent = sanitizedRawHtml ? <div dangerouslySetInnerHTML={{
    __html: sanitizedRawHtml
  }} /> : null;
  useEffect(() => {
    if (!globalThis?.window) return;
    const handleMessage = (e: MessageEvent) => {
      if (!allowedYextOrigins.includes(e.origin)) return;
      if (typeof e.data !== 'string') return;
      try {
        const msg = JSON.parse(e.data) as IDataLayerEventConfig;
        dataLayerConfig({
          ...msg
        });
      } catch {
        // Ignore malformed cross-window message payloads.
      }
    };
    globalThis.window.addEventListener('message', handleMessage, false);
    return () => {
      globalThis.window.removeEventListener('message', handleMessage, false);
    };
  }, []);
  if (hasRawHtml) {
    return rawHtmlContent;
  }
  return <Box data-testid={dataTestId} width="100%" height={iFrameHeight} sx={{
    position: 'relative',
    overflow: 'hidden'
  }} data-sentry-element="Box" data-sentry-component="IFrame" data-sentry-source-file="Iframe.tsx">
			{url ? <iframe src={url} allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowFullScreen style={{
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
      width: '100%',
      height: '100%',
      border: 'none'
    }} title={title} /> : null}
		</Box>;
}